import { useState, type ReactNode } from "react";
import { postJson } from "../api";
import { useAiData, useTitle } from "../ai/parts";
import {
  Counter,
  DataTable,
  MobileActions,
  RadioCards,
  RowsSkeleton,
  SearchBox,
  Section,
  useFilters,
  useIsMobile,
} from "../ai/widgets";
import ui from "../ai/ai.module.css";
import { OrgNotFound } from "../pages/StatePages";
import { useOpenOrg } from "../shell/openOrg";
import { formatText, useTexts } from "../texts";
import { ChoiceMenu } from "../ui/ChoiceMenu";
import { ChatIcon } from "../ui/icons/Icon";
import { Notice, PageHead, useToast } from "../ui/kit";
import { ListFooter, useServerList } from "../ui/list";
import { PageState } from "../ui/pieces";
import { plural, statusKey, userHref, whenIn, type PersonStatus } from "./organizations";
import { Initials } from "./PeoplePage";
import { ConfirmWindow, usePersonActions, type Person } from "./PersonActions";
import {
  AccessFacts,
  AttemptsList,
  CloseSessionWindow,
  SessionsBlock,
  type OpenSession,
  type SecurityState,
} from "./SecurityBlocks";
import {
  deviceName,
  expirationLabel,
  expirationOptions,
  looksLikeEmail,
  mfaKey,
  mfaModes,
  policyImpact,
} from "./security";
import styles from "./orgs.module.css";
import own from "./security.module.css";

type OrgHead = { id: number; name: string; code: string; timezone: string };

type Tab = "policies" | "sessions" | "recover";

/** Encabezado «Seguridad y acceso» con su acción y las pestañas Políticas · Sesiones · Recuperar acceso (C03). */
function SecurityHead({ orgId, current, action }: { orgId: number; current: Tab; action?: ReactNode }) {
  const { L } = useTexts();
  const tabs: { key: Tab; label: string; href: string }[] = [
    { key: "policies", label: L("ADM_SEC_TAB_POLICIES"), href: `/Organizations/Politicas?id=${orgId}` },
    { key: "sessions", label: L("ADM_SEC_TAB_SESSIONS"), href: `/Organizations/Sesiones?id=${orgId}` },
    { key: "recover", label: L("ADM_SEC_TAB_RECOVER"), href: `/Organizations/RecuperacionAcceso?id=${orgId}` },
  ];
  return (
    <>
      <PageHead title={L("ADM_ORG_SECURITY")}>{action}</PageHead>
      <nav className={`tm-tabs ${ui.tabs}`} aria-label={L("ADM_ORG_SECURITY")} data-part="pestanas">
        {tabs.map((tab) => (
          <a key={tab.key} href={tab.href} aria-current={tab.key === current ? "page" : undefined}>
            {tab.label}
          </a>
        ))}
      </nav>
    </>
  );
}

/** Estado de página cuando la lectura falla: sin permiso (C40) o no disponible con «Reintentar» (nunca 0, H-3). */
function Failed({ head, status, retry }: { head: ReactNode; status: number | undefined; retry: () => void }) {
  const { L } = useTexts();
  return (
    <PageState
      head={head}
      title={status === 403 ? L("ADM_FORBIDDEN") : L("ADM_UNAVAILABLE")}
      line={status === 403 ? L("ADM_FORBIDDEN_LINE") : L("ADM_UNAVAILABLE_LINE")}
      action={
        status === 403 ? undefined : (
          <button type="button" className="tm-btn secondary" onClick={retry}>
            {L("ADM_RETRY")}
          </button>
        )
      }
    />
  );
}

type Policies = {
  organization: OrgHead;
  passwordExpiration: string;
  mfaMode: string;
  people: { active: number; admins: number } | null;
};

/**
 * `emp-politicas` · Seguridad y acceso › Políticas (§5.5, D9): vencimiento de contraseñas (C08) y verificación en dos
 * pasos (C08 tarjetas). Guardar abre la confirmación `politica` con el impacto y su número; sin la casilla «Revisé el
 * impacto». Si la lectura falla no hay formulario (nunca valores por defecto).
 */
export function PoliciesPage({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  const data = useAiData<Policies>(`/api/admin/organizations/${orgId}/policies`);
  useOpenOrg(data.data?.organization ?? null);
  useTitle(`${L("ADM_ORG_SECURITY")} · ${L("ADM_SEC_TAB_POLICIES")}`);
  if (data.state === "missing") return <OrgNotFound />;
  const policies = data.data;
  if (!policies) {
    const head = <SecurityHead orgId={orgId} current="policies" />;
    if (data.state === "failed") return <Failed head={head} status={data.error?.status} retry={data.retry} />;
    return (
      <>
        {head}
        <PoliciesSkeleton />
      </>
    );
  }
  return (
    <PoliciesForm
      key={`${policies.passwordExpiration}|${policies.mfaMode}`}
      orgId={orgId}
      policies={policies}
      onSaved={() => void data.reload()}
    />
  );
}

/** C22 · el esqueleto con la misma vista: subtítulo, campo y las cuatro tarjetas. */
function PoliciesSkeleton() {
  return (
    <div className={own.policies} role="status" aria-busy="true" data-part="cargando">
      <span className={ui.bone} style={{ width: 240, height: 22 }} />
      <span className={ui.bone} style={{ width: 360 }} />
      <span className={ui.bone} style={{ width: "min(566px, 100%)", height: 42 }} />
      <span className={ui.bone} style={{ width: 240, height: 22, marginTop: 24 }} />
      <div className={own.mfaGrid}>
        {[0, 1, 2, 3].map((index) => (
          <span key={index} className={ui.bone} style={{ height: 64 }} />
        ))}
      </div>
    </div>
  );
}

function PoliciesForm({ orgId, policies, onSaved }: { orgId: number; policies: Policies; onSaved: () => void }) {
  const { L, locale } = useTexts();
  const toast = useToast();
  const [expiration, setExpiration] = useState(policies.passwordExpiration);
  const [mfa, setMfa] = useState(policies.mfaMode);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const before = { expiration: policies.passwordExpiration, mfa: policies.mfaMode };
  const options = [
    ...expirationOptions.map((value) => ({ value, label: expirationLabel(L, value) })),
    // CUSTOM no se elige: si ya es el valor de la empresa se muestra como dato y se conserva al guardar.
    ...(policies.passwordExpiration === "CUSTOM" ? [{ value: "CUSTOM", label: expirationLabel(L, "CUSTOM") }] : []),
  ];
  const save = () => {
    const impact = policyImpact(L, locale, before, { expiration, mfa }, policies.people);
    if (!impact) return toast.show(L("ADM_SEC_NO_CHANGES"));
    setError(null);
    setConfirming(impact);
  };
  const button = (
    <button type="button" className="tm-btn primary" onClick={save}>
      {L("ADM_PPL_SAVE")}
    </button>
  );
  return (
    <>
      <SecurityHead orgId={orgId} current="policies" action={<span className={ui.desktopInline}>{button}</span>} />
      <div className={own.policies}>
        {error ? <Notice tone="error">{error}</Notice> : null}
        <section aria-labelledby="pol-exp">
          <h2 id="pol-exp" className={ui.subtitle}>
            {L("ADM_SEC_EXP_TITLE")}
          </h2>
          <p className={ui.sectionLine}>{L("ADM_SEC_EXP_LINE")}</p>
          <div className={`tm-field ${own.expField}`}>
            <label htmlFor="pol-expiration">{L("ADM_SEC_EXP_LABEL")}</label>
            <ChoiceMenu
              id="pol-expiration"
              label={L("ADM_SEC_EXP_LABEL")}
              value={expiration}
              options={options.map((option) => ({ ...option, disabled: option.value === "CUSTOM" }))}
              onChange={setExpiration}
              describedBy="pol-expiration-help"
              chevron={<ChatIcon name="chevron-down" />}
              check={<ChatIcon name="check" />}
            />
            <small id="pol-expiration-help" className="tm-field-help">
              {L(policies.passwordExpiration === "CUSTOM" ? "ADM_SEC_EXP_HELP_CUSTOM" : "ADM_SEC_EXP_HELP")}
            </small>
          </div>
        </section>
        <section aria-labelledby="pol-mfa">
          <h2 id="pol-mfa" className={ui.subtitle}>
            {L("ADM_PPL_MFA")}
          </h2>
          <div className={own.mfaCards}>
            <RadioCards
              label={L("ADM_PPL_MFA")}
              value={mfa}
              onChange={setMfa}
              options={mfaModes.map((value) => ({
                value,
                title: L(mfaKey[value]!.title),
                line: L(mfaKey[value]!.line),
              }))}
            />
          </div>
        </section>
      </div>
      <MobileActions>{button}</MobileActions>
      <ConfirmWindow
        open={!!confirming}
        danger={false}
        title={L("ADM_SEC_SAVE_TITLE")}
        line={confirming ?? ""}
        action={L("ADM_PPL_SAVE")}
        onClose={() => setConfirming(null)}
        onConfirm={async () => {
          const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
            `/api/admin/organizations/${orgId}/policies`,
            { passwordExpiration: expiration, mfaMode: mfa },
          );
          if (reply.status === 200 && reply.body?.saved) {
            toast.show(L("ADM_SEC_SAVED"));
            onSaved();
            return true;
          }
          // Lo elegido se conserva; el motivo queda arriba del formulario (C14).
          setError(L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"));
          return true;
        }}
      />
    </>
  );
}

type OrgSessionRow = OpenSession & { userId: number; name: string; email: string };

/**
 * `emp-sesiones` · Seguridad y acceso › Sesiones (§5.5, B3): las sesiones abiertas de la empresa paginadas en Identity
 * (antes, una llamada por persona). La fila abre la ficha de la persona en Seguridad; «Cerrar» confirma (D8).
 */
export function SessionsPage({ orgId }: { orgId: number }) {
  const { L, locale } = useTexts();
  const mobile = useIsMobile();
  const list = useServerList<OrgSessionRow>(`/api/admin/organizations/${orgId}/sessions`);
  const org = (list.extra?.organization as OrgHead | undefined) ?? null;
  const people = (list.extra?.people as number | undefined) ?? 0;
  const [closing, setClosing] = useState<OrgSessionRow | null>(null);
  useOpenOrg(org);
  useTitle(`${L("ADM_ORG_SECURITY")} · ${L("ADM_SEC_TAB_SESSIONS")}`);
  if (list.state === "notFound") return <OrgNotFound />;
  const head = <SecurityHead orgId={orgId} current="sessions" />;
  if (list.state === "forbidden")
    return <PageState head={head} title={L("ADM_FORBIDDEN")} line={L("ADM_FORBIDDEN_LINE")} />;
  const zone = org?.timezone ?? "America/Lima";
  const href = (row: OrgSessionRow) => userHref(orgId, row.userId, undefined, "seguridad");
  return (
    <>
      {head}
      {list.state === "loading" ? (
        <RowsSkeleton rows={5} table={!mobile} />
      ) : list.state === "error" ? (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={list.reload}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      ) : list.items.length === 0 ? (
        <div className="tm-empty" role="status" data-part="vacio">
          <strong>{L("ADM_SEC_NO_SESSIONS")}</strong>
          <span>{L("ADM_SEC_NO_SESSIONS_LINE")}</span>
          <a className="tm-btn secondary" href={`/Organizations/Usuarios?id=${orgId}`}>
            {L("ADM_SEC_SEE_PEOPLE")}
          </a>
        </div>
      ) : (
        <>
          <Counter>
            {`${plural(L, locale, list.total, "ADM_SEC_N_SESSIONS_ONE", "ADM_SEC_N_SESSIONS")} · ${plural(
              L,
              locale,
              people,
              "ADM_SEC_N_PEOPLE_ONE",
              "ADM_SEC_N_PEOPLE",
            )}`}
          </Counter>
          {mobile ? (
            // C17 · iniciales, nombre y una línea con correo y dispositivo; la fila abre la ficha › Seguridad.
            <ul className={`tm-list ${ui.list}`} aria-label={L("ADM_SEC_TAB_SESSIONS")} data-part="lista">
              {list.items.map((row) => (
                <li key={row.sessionId} className={ui.row}>
                  <a className={ui.rowLinkArea} href={href(row)}>
                    <Initials name={row.name} seed={row.userId} large />
                    <span className={ui.rowText}>
                      <span className={ui.line1}>
                        <strong>{row.name}</strong>
                      </span>
                      <span className={`${ui.line2} ${styles.oneLine}`}>
                        {[row.email, deviceName(L, row), whenIn(L, locale, row.lastActivityAt, zone)].join(" · ")}
                      </span>
                    </span>
                    <span className={ui.rowEnd} aria-hidden="true">
                      <ChatIcon name="chevron-right" />
                    </span>
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <div className={styles.tableWrap}>
              <DataTable
                label={L("ADM_SEC_TAB_SESSIONS")}
                rows={list.items}
                rowKey={(row) => row.sessionId}
                href={href}
                columns={[
                  {
                    key: "person",
                    title: L("ADM_SEC_COL_PERSON"),
                    render: (row) => (
                      <span className={styles.person}>
                        <Initials name={row.name} seed={row.userId} />
                        <span className={styles.personText}>
                          <strong>{row.name}</strong>
                          <span>{row.email}</span>
                        </span>
                      </span>
                    ),
                  },
                  { key: "device", title: L("ADM_PPL_COL_DEVICE"), render: (row) => deviceName(L, row) },
                  { key: "ip", title: L("ADM_PPL_COL_IP"), render: (row) => row.ip ?? "—" },
                  {
                    key: "last",
                    title: L("ADM_PPL_COL_ACTIVITY"),
                    render: (row) => whenIn(L, locale, row.lastActivityAt, zone),
                  },
                  {
                    key: "close",
                    title: "",
                    render: (row) => (
                      <span className={own.endCell}>
                        <button
                          type="button"
                          className="tm-btn secondary small"
                          aria-label={formatText(L("ADM_SEC_CLOSE_OF"), row.name, deviceName(L, row))}
                          onClick={() => setClosing(row)}
                        >
                          {L("ADM_PPL_CLOSE")}
                        </button>
                      </span>
                    ),
                  },
                ]}
                mobile={(row) => ({ title: row.name, line: row.email })}
              />
            </div>
          )}
          <ListFooter list={list} />
        </>
      )}
      <CloseSessionWindow
        orgId={orgId}
        userId={closing?.userId ?? 0}
        name={closing?.name ?? ""}
        session={closing}
        onClose={() => setClosing(null)}
        onDone={list.reload}
      />
    </>
  );
}

type Found = {
  organization: OrgHead;
  state: "found" | "other" | "none" | "empty";
  member?: {
    userId: number;
    name: string;
    email: string;
    status: PersonStatus;
    isOwner: boolean;
    accountStatus: string;
    locked: boolean;
  };
  security?: SecurityState | null;
  sessions?: OpenSession[] | null;
};

/**
 * `emp-recuperar` · Seguridad y acceso › Recuperar acceso (§5.5, D10, B7): el correo se busca solo entre las personas
 * de esta empresa y queda en la dirección. Una cuenta de otra empresa no da ningún dato. Los bloques Estado de acceso,
 * Intentos y Sesiones son los de la ficha (E48); Desbloquear con «Deshacer», Cerrar todas confirma.
 */
export function RecoverPage({ orgId }: { orgId: number }) {
  const { L } = useTexts();
  const { get, set } = useFilters();
  const email = get("email").trim();
  const valid = looksLikeEmail(email);
  // Sin correo (o mientras no tiene forma de correo) se pide solo la empresa, para el título y los textos.
  const data = useAiData<Found>(
    `/api/admin/organizations/${orgId}/members/by-email${valid ? `?email=${encodeURIComponent(email)}` : ""}`,
  );
  const org = data.data?.organization ?? null;
  useOpenOrg(org);
  useTitle(`${L("ADM_ORG_SECURITY")} · ${L("ADM_SEC_TAB_RECOVER")}`);
  const [version, setVersion] = useState(0);
  const reload = () => {
    void data.reload();
    setVersion((value) => value + 1);
  };
  const actions = usePersonActions(orgId, org?.name ?? "", reload);
  const toast = useToast();
  if (data.state === "missing") return <OrgNotFound />;
  const head = <SecurityHead orgId={orgId} current="recover" />;
  const search = (
    <div className={own.recoverSearch}>
      <SearchBox
        id="recover-email"
        value={email}
        placeholder={L("ADM_SEC_RECOVER_SEARCH")}
        onChange={(value) => set({ email: value })}
      />
    </div>
  );
  const orgName = org?.name ?? "";
  let body: ReactNode;
  if (!email)
    body = (
      <div className="tm-empty" role="status" data-part="vacio">
        <strong>{L("ADM_SEC_RECOVER_EMPTY")}</strong>
        <span>{formatText(L("ADM_SEC_RECOVER_EMPTY_LINE"), orgName)}</span>
      </div>
    );
  else if (!valid) body = <p className={ui.note}>{L("ADM_SEC_RECOVER_TYPE_EMAIL")}</p>;
  else if (data.state === "failed")
    body =
      data.error?.status === 403 ? (
        <Notice tone="error">{L("ADM_FORBIDDEN_LINE")}</Notice>
      ) : (
        <Notice
          tone="error"
          action={
            <button type="button" className="tm-btn secondary" onClick={data.retry}>
              {L("ADM_RETRY")}
            </button>
          }
        >
          {L("ADM_UNAVAILABLE_LINE")}
        </Notice>
      );
  else if (!data.data) body = <RowsSkeleton rows={3} />;
  else if (data.data.state === "other")
    body = <Notice tone="warning">{formatText(L("ADM_SEC_RECOVER_OTHER"), orgName)}</Notice>;
  else if (data.data.state === "none" || !data.data.member)
    body = (
      <div className="tm-empty" role="status" data-part="vacio">
        <strong>{formatText(L("ADM_SEC_RECOVER_NONE"), orgName)}</strong>
        <button type="button" className="tm-btn secondary" onClick={() => set({ email: "" })}>
          {L("ADM_AI_CLEAR_SEARCH")}
        </button>
      </div>
    );
  else {
    const found = data.data;
    const member = found.member!;
    const zone = found.organization.timezone;
    const person: Person = {
      userId: member.userId,
      name: member.name,
      status: member.status,
      isOwner: member.isOwner,
      sessions: found.sessions?.length ?? null,
    };
    const account = member.accountStatus === "ACTIVE" ? L("ADM_PPL_ACTIVE_F") : L("ADM_PPL_INACTIVE_F");
    const sendRecovery = async () => {
      const reply = await postJson<{ saved?: boolean; messageKey?: string }>(
        `/api/admin/organizations/${orgId}/members/${member.userId}/password-recovery`,
        {},
      );
      toast.show(
        reply.status === 200 && reply.body?.saved
          ? formatText(L("ADM_SEC_RECOVERY_SENT"), member.email)
          : L(reply.body?.messageKey ?? "ADMIN_ERR_GENERIC"),
      );
    };
    body = (
      <>
        <div className={styles.ownerRow} data-part="persona">
          <Initials name={member.name} seed={member.userId} large />
          <span className={`${styles.ownerText} ${own.grow}`}>
            <span className={styles.ownerName}>
              {member.name}
              {member.status === "blocked" ? <span className="tm-tag ochre">{L(statusKey("blocked"))}</span> : null}
            </span>
            <span className={styles.ownerLine}>
              {member.email} · {formatText(L("ADM_SEC_BELONGS"), orgName)}
            </span>
          </span>
          <a className="tm-btn secondary" href={userHref(orgId, member.userId, undefined, "seguridad")}>
            {L("ADM_SEC_OPEN_RECORD")}
          </a>
        </div>
        {found.security ? (
          <AccessFacts security={found.security} zone={zone} account={account} />
        ) : (
          <Notice tone="warning">{L("ADM_PPL_SECURITY_UNAVAILABLE")}</Notice>
        )}
        <div className={own.recoverActions} data-part="acciones-recuperar">
          {member.status === "blocked" ? (
            <button type="button" className="tm-btn primary" onClick={() => void actions.unlock(person)}>
              {L("ADM_PPL_UNLOCK")}
            </button>
          ) : null}
          <button type="button" className="tm-btn secondary" onClick={() => void sendRecovery()}>
            {L("ADM_SEC_SEND_RECOVERY")}
          </button>
          {found.sessions && found.sessions.length > 0 ? (
            <button type="button" className="tm-btn secondary" onClick={() => actions.openRevoke(person)}>
              {L("ADM_SEC_CLOSE_ALL_THEIRS")}
            </button>
          ) : null}
        </div>
        <Section id="rec-attempts" title={L("ADM_PPL_ATTEMPTS")}>
          <AttemptsList orgId={orgId} userId={member.userId} zone={zone} version={version} />
        </Section>
        <Section id="rec-sessions" title={L("ADM_PPL_SESSIONS")}>
          <SessionsBlock
            orgId={orgId}
            userId={member.userId}
            name={member.name}
            sessions={found.sessions ?? null}
            zone={zone}
            onChanged={reload}
          />
        </Section>
        {actions.windows}
      </>
    );
  }
  return (
    <>
      {head}
      {search}
      {body}
    </>
  );
}
